<template>
  <div>
    <naj-box :title="local.bankruptcy.subRequests.requestApplicantInfo">
      <div>
        <request-applicant
          :form-model="formModel.requestApplicant"
          :legal-capacity-id="formModel.requestApplicant.legalCapacityId"
          :audit-confirmations="auditConfirmations"
          :is-view-mode="formModel.mainRequest.isRequestOwner || !isNewMode"
          :is-new-mode="isNewMode"
          :is-edit-mode="isNewMode"
          :is-not-completed="isNewMode"
        >
          <template
            slot="prefix"
            slot-scope="{ }"
          >
            <v-col
              cols="12"
              md="6"
              lg="3"
            >
              <naj-input
                :type-id="formFieldType.selectbox"
                :value.sync="formModel.requestApplicant.legalCapacityId"
                :label="local.bankruptcy.subRequests.requestApplicantForm.legalCapacity"
                :items="allowedLegalCapacities"
                item-value="id"
                item-text="name"
                :validates="`required`"
                :disabled="formModel.mainRequest.isRequestOwner ||isViewMode || isEditMode || loading"
              ></naj-input>
            </v-col>
          </template>
        </request-applicant>
        <v-row>
          <v-col
            cols="12"
            md="6"
            lg="3"
          >
            <naj-input
              :type-id="formFieldType.selectbox"
              :value.sync="formModel.requestApplicant.requestOwnerTypeId"
              :label="local.bankruptcy.subRequests.requestApplicantForm.onBehalfOf"
              :items="allowedRequestOwnerTypes"
              item-value="id"
              item-text="name"
              :validates="`required`"
              :disabled="formModel.mainRequest.isRequestOwner ||isViewMode || isEditMode || loading"
              @change="requestOwnerTypeChanged"
            ></naj-input>
          </v-col>
          <v-col
            v-if="isStakeholder"
            cols="12"
            md="6"
            lg="3"
          >
            <naj-input
              :type-id="formFieldType.selectbox"
              :value.sync="formModel.requestApplicant.stakeholderTypeId"
              :label="local.bankruptcy.subRequests.requestApplicantForm.stakeholderType"
              :items="allowedStackholderTypes"
              item-value="id"
              item-text="name"
              :validates="`required`"
              :disabled="formModel.mainRequest.isRequestOwner ||isViewMode || isEditMode || loading"
              @change="stakeholderTypeChanged"
            ></naj-input>
          </v-col>

          <v-col
            v-if="isTrusteeOwnerType && hasMultipleTrustees"
            cols="12"
            md="6"
            lg="6"
          >
            <naj-input
              :type-id="formFieldType.selectbox"
              :value.sync="formModel.requestApplicant.trusteeIdentityNumber"
              :label="trusteesLabel"
              :items="individualTrustees"
              item-value="identityNumber"
              item-text="name"
              :validates="`required`"
              :disabled="formModel.mainRequest.isRequestOwner || isViewMode || isEditMode || loading"
              @change="trusteeChanged"
            ></naj-input>
          </v-col>

          <v-col cols="12">
            <naj-data-table
              v-if="isTrusteeOwnerType && selectedTrustee"
              no-header
              :headers="personHeader"
              :loading="loading"
              :items="trusteeData"
              :disable-items-per-page="true"
              no-margin-bottom
            ></naj-data-table>
            <debtor-form
              v-if="isDebtor || isCompetentParty"
              :form-model="formModel.requestApplicant.debtor"
              :loading="true"
            ></debtor-form>
            <bankrupt-debts-form
              v-if="isCreditor || isRequestOwnerCreditor"
              :form-model="formModel.requestApplicant.creditor"
              :loading="loading || !isNewMode || isRequestOwnerCreditor|| formModel.requestApplicant.creditor.isRetrieved"
              :is-view-mode="!isNewMode || isRequestOwnerCreditor"
              @isIdentityVerified="creditorIdentityVerified"
            ></bankrupt-debts-form>
            <debtor-employee-form
              v-if="isDebtorEmployee"
              :form-model="formModel.requestApplicant.debtorEmployee "
              :loading="loading || !isNewMode || formModel.requestApplicant.debtorEmployee.isRetrieved"
              :is-view-mode="!isNewMode"
              @isIdentityVerified="debtorEmployeeIdentityVerified"
            ></debtor-employee-form>
            <partner-form
              v-if="isPartner"
              :form-model="formModel.requestApplicant.partner"
              :loading="loading || !isNewMode || formModel.requestApplicant.partner.isRetrieved"
              :is-edit-mode="isEditMode"
              @isIdentityVerified="partnerIdentityVerified"
            ></partner-form>
            <interest-party-form
              v-if="isInterestParty"
              :form-model="formModel.requestApplicant.interestedParty"
              :loading="loading || !isNewMode || formModel.requestApplicant.interestedParty.isRetrieved"
              :is-edit-mode="isEditMode"
              @isIdentityVerified="interestPartyIdentityVerified"
            ></interest-party-form>
          </v-col>
        </v-row>
      </div>
    </naj-box>

    <attorney-form
      v-if="showAttorneyForm && requestType.showAttorneyForm"
      :form-model="formModel.requestApplicant"
      :audit-confirmations="auditConfirmations"
      :is-view-mode="!isNewMode"
      :is-new-mode="isNewMode"
      :is-edit-mode="isEditMode"
      :is-not-completed="true"
      :agent="agent"
      :identity-number="formModel.requestApplicant.identityNumber"
    ></attorney-form>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapLUTState, mapActions } = createNamespacedHelpers(
  'BankruptcyModule'
);
const { mapState: mapGlobalState, mapActions: mapGlobalActions } = createNamespacedHelpers('GlobalModule');
import { toHijriDate } from '@/helpers/utils';
import { socialIdentityTypeEnum, countriesEnum, userType, socialIdTypeEnum } from '@/helpers/enum';
import { requestOwnerTypeEnum, legalCapacitiesEnum, stakeholderTypesEnum, partyTypeEnum, subRequestType } from '@/modules/bankruptcy/helpers/enum';
import RequestApplicant from '../request/steps/basic-data/request-applicant/index.vue';
import AttorneyForm from '../request/steps/request-applicant-data/attorney-form.vue';
import BankruptDebtsForm from '../request/steps/request-details/bankrupt-debts-tab/bankrupt-debts-form.vue';
import PartnerForm from '../request/steps/request-details/partners-tab/partner-form.vue';
import InterestPartyForm from '../request/steps/request-details/interested-party-tab/interested-party-form.vue';
import DebtorEmployeeForm from '../request/steps/request-details/debtor-employees-tab/debtor-employee-form.vue';
import DebtorForm from './debtor-form.vue';

export default {
  name: 'SubRequestApplicant',
  inject: ['$validator'],
  components: {
    RequestApplicant,
    AttorneyForm,
    BankruptDebtsForm,
    PartnerForm,
    InterestPartyForm,
    DebtorEmployeeForm,
    DebtorForm,
  },
  props: {
    formModel: {
      type: Object,
      required: true,
    },
    requestType: {
      type: Object,
      required: true,
    },
    isNewMode: {
      type: Boolean,
      required: true,
    },
    isViewMode: {
      type: Boolean,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      userType,
      loading: false,
      stakeholderTypesEnum,
      requestOwnerTypeEnum,
      partyTypeEnum,
      toHijriDate,
      mainRequestParties: {},
      applicants: [],
      isApplicantDisabled: false,
      newCreditorKey: 'new',
      bankruptDebt: {},
      legalCapacitiesEnum,
      auditConfirmations: {},
      selectedTrustee: null,
      trusteeData: [],
      subRequestType,
      trusteesLabel: (this.local.bankruptcy.subRequests.addRepresentative.bankruptcyTrustee),
      personHeader: [
        {
          value: 'fullName',
          text: this.local.bankruptcy.identityVerificationForm.theName,
        },
        {
          value: 'identityNumber',
          text: this.local.bankruptcy.requestDetails.partners.identityNumber,
        },
        {
          value: 'identityTypeName',
          text: this.local.bankruptcy.requestDetails.partners.identityType,
        },
      ],
    };
  },
  computed: {
    ...mapLUTState([
      'mainRequestCategories',
      'allRequestOwnerTypes',
      'legalCapacities',
      'stakeholderTypes',
      'currentUser']),
    ...mapGlobalState(['userInfo']),
    // eslint-disable-next-line vue/return-in-computed-property
    allowedLegalCapacities() {
      if (this.formModel.mainRequest.isRequestOwner || this.isViewMode)
        return this.legalCapacities;
      else
      {
        if (this.userInfo.userType === this.userType.najizCitizen && this.isUserAsignedTrustee) {
          return this.legalCapacities.filter(lc => lc.id == legalCapacitiesEnum.legalEntityRepresentative || lc.id == legalCapacitiesEnum.bankruptcySecretary);
        }
        else if (this.userInfo.userType === this.userType.najizCitizen) {
          this.$set(this.formModel.requestApplicant, 'legalCapacityId', legalCapacitiesEnum.legalEntityRepresentative);
          return this.legalCapacities.filter(lc => lc.id == legalCapacitiesEnum.legalEntityRepresentative);
        }
        else if (this.userInfo.userType === this.userType.najizLawyer) {
          this.$set(this.formModel.requestApplicant, 'legalCapacityId', legalCapacitiesEnum.lawyer);
          return this.legalCapacities.filter(lc => lc.id == legalCapacitiesEnum.lawyer);
        }
      }
    },
    allowedStackholderTypes() {
      if (this.isExternalVerdictObjectionRequest) {
        switch (this.formModel.requestApplicant.legalCapacityId) {
          case legalCapacitiesEnum.lawyer:
            return this.stakeholderTypes.filter(st =>
              st.id == stakeholderTypesEnum.creditor ||
              st.id == stakeholderTypesEnum.partner ||
              st.id == stakeholderTypesEnum.employee ||
              st.id == stakeholderTypesEnum.interestParty
            );
          case legalCapacitiesEnum.legalEntityRepresentative:
            return this.stakeholderTypes.filter(st =>
              st.id == stakeholderTypesEnum.creditor ||
              st.id == stakeholderTypesEnum.partner
            );
          case legalCapacitiesEnum.bankruptcySecretary:
            return [];
        }
      }
      return this.stakeholderTypes.filter(st =>
        st.id == stakeholderTypesEnum.creditor ||
        st.id == stakeholderTypesEnum.partner ||
        st.id == stakeholderTypesEnum.employee);
    },
    allowedRequestOwnerTypes() {
      if (this.isExternalVerdictObjectionRequest) {
        switch (this.formModel.requestApplicant.legalCapacityId) {
          case legalCapacitiesEnum.lawyer:
            return this.allRequestOwnerTypes.filter(lc =>
              lc.id == requestOwnerTypeEnum.debtor ||
              lc.id == requestOwnerTypeEnum.stakeholder ||
              (lc.id == requestOwnerTypeEnum.trustee && this.hasPersonTrustee)
            );
          case legalCapacitiesEnum.legalEntityRepresentative:
            return this.allRequestOwnerTypes.filter(lc =>
              lc.id == requestOwnerTypeEnum.stakeholder ||
              lc.id == requestOwnerTypeEnum.debtor
            );
          case legalCapacitiesEnum.bankruptcySecretary:
            return this.allRequestOwnerTypes.filter(lc =>
              lc.id == requestOwnerTypeEnum.debtor ||
              lc.id == requestOwnerTypeEnum.trustee
            ).map((x) => {
              if (x.id == requestOwnerTypeEnum.trustee) {
                x.name = this.local.bankruptcy.subRequests.requestInquiry.behalfHimSelf;
              }
              return x;
            }
            );
          default:
            return [];
        }
      }
      if (this.formModel.mainRequest.isRequestOwner) return this.allRequestOwnerTypes.filter(lc => lc.id != requestOwnerTypeEnum.trustee);
      else if (this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.creditor) {
        return this.allRequestOwnerTypes.filter(lc => lc.id == requestOwnerTypeEnum.creditor);
      } else if (this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.competentParty) {
        return this.allRequestOwnerTypes.filter(lc => lc.id == requestOwnerTypeEnum.competentParty);
      }
      else {
        if(this.isTrusteeObjectionRequest){
          return this.allRequestOwnerTypes.filter(lc =>
              lc.id == requestOwnerTypeEnum.debtor ||
              lc.id == requestOwnerTypeEnum.trustee
            ).map((x) => {
              if (x.id == requestOwnerTypeEnum.trustee) {
                x.name = this.local.bankruptcy.subRequests.requestInquiry.behalfHimSelf;
              }
              return x;
            });
        }
        return this.allRequestOwnerTypes.filter(lc =>
          (lc.id == requestOwnerTypeEnum.stakeholder) ||
        lc.id == requestOwnerTypeEnum.debtor ||
        (lc.id == requestOwnerTypeEnum.trustee && ((this.formModel?.individualTrustees || []).length > 0 || !!this.formModel.requestApplicant?.trustee)));
      }
    },
    showAttorneyForm() {
      return ((this.formModel.requestApplicant.requestOwnerTypeId && this.formModel.requestApplicant.legalCapacityId == legalCapacitiesEnum.lawyer) ||
        (this.isExternalVerdictObjectionRequest && this.formModel.requestApplicant.requestOwnerTypeId && this.formModel.requestApplicant.legalCapacityId == legalCapacitiesEnum.legalEntityRepresentative));
    },
    isCreditor() {
      return this.formModel.requestApplicant.stakeholderTypeId == stakeholderTypesEnum.creditor
        || (this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.creditor && this.formModel.mainRequest.isRequestOwner);
    },
    isPartner() {
      return this.formModel.requestApplicant.stakeholderTypeId == stakeholderTypesEnum.partner;
    },
    isInterestParty() {
      return this.formModel.requestApplicant.stakeholderTypeId == stakeholderTypesEnum.interestParty;
    },
    isDebtorEmployee() {
      return this.formModel.requestApplicant.stakeholderTypeId == stakeholderTypesEnum.employee;
    },
    isStakeholder() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.stakeholder;
    },
    isDebtor() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.debtor;
    },
    isCompetentParty() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.competentParty;
    },
    isRequestOwnerCreditor() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.creditor;
    },
    isTrusteeOwnerType() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.trustee;
    },
    isExternalVerdictObjectionRequest() {
      return (
        this.$route.meta.isExternalVerdictObjectionRequest === true
      );
    },
    hasPersonTrustee() {
      return this.formModel.trustees?.some(t => t.identityTypeId === partyTypeEnum.person);
    },
    isUserAsignedTrustee() {
      return !!this.formModel.trustees?.some(t => t.identityNumber === this.userInfo.socialId);
    },
    isTrusteeObjectionRequest() {
      return this.subRequestType[this.$route.params.requestType] == subRequestType.VerdictObjectionRequest && this.isUserAsignedTrustee;
    },
    individualTrustees() {
      const currentIdentity = this.currentUser?.socialId;
      var individuals = (this.formModel.trustees || [])
        .filter(t => t.identityTypeId === partyTypeEnum.person);
      if(individuals.some(s => s.identityNumber === currentIdentity) && !this.isViewMode){
        individuals = individuals.filter(t => t.identityNumber === currentIdentity);
      }
      return individuals
    },
    hasMultipleTrustees() {
      return (this.formModel?.trustees || []).length > 1;
    },
    agent() {
      if (this.isDebtor) return this.formModel.requestApplicant.debtor;
      if (this.isCreditor || this.isRequestOwnerCreditor) return this.formModel.requestApplicant.creditor;
      if (this.isPartner) return this.formModel.requestApplicant.partner;
      if (this.isDebtorEmployee) return this.formModel.requestApplicant.debtorEmployee;
      if (this.isInterestParty) return this.formModel.requestApplicant.interestedParty;
      if (this.isTrusteeOwnerType) return {
        ...this.formModel.requestApplicant.trustee,
        typeId: partyTypeEnum.person,
      };
      return {};
    },
    inquiryFields() {
      return ['identityTypeId', 'identityNumber', 'commercialRegistrationNumber', 'unifiedNumber', 'typeId'];
    },
  },
  watch: {},
  mounted() {
    this.getLegalCapacities();
    this.getStakeholderTypes();
    this.getAllRequestOwnerTypes();
    if (this.isNewMode || this.isTrusteeOwnerType) {
      this.loadData();
    }
  },
  methods: {
    ...mapGlobalActions(['getUserInfo']),
    ...mapActions([
      'getMainRequestParties',
      'getMainRequestCategories',
      'getAllRequestOwnerTypes',
      'getLegalCapacities',
      'getStakeholderTypes',
      'inquiryMainRequestPartner',
      'inquiryMainRequestCreditor',
      'inquiryMainRequestDebtorEmployees',
      'inquiryMainRequestInterestParty',
      'getMainRequestSecretariesApproved',
    ]),
    loadData() {
      this.loading = true;
      Promise.all([
        this.getUserInfo(),
        this.getMainRequestParties(this.formModel.mainRequest.objectKey),
        this.getMainRequestSecretariesApproved(this.formModel.mainRequest.objectKey),
      ])
        .then((res) => {
          this.mainRequestParties = res[1].data;
          this.$set(this.formModel, 'trustees', res[2]?.data);
          if (this.formModel.mainRequest.isRequestOwner) {
            this.$set(this.formModel, 'requestApplicant', this.mainRequestParties.requestApplicant);
            this.$set(this.formModel.requestApplicant, 'debtor', this.mainRequestParties.debtor);
            this.$set(this.formModel.requestApplicant, 'creditor', this.mainRequestParties.creditor);
            this.$set(this.formModel, 'applicantDetailsId', this.mainRequestParties.requestApplicant.id);
          } else {
            this.setCurrenUserInfo();
          }
          this.autoSelectTrustee();
        })
        .finally(() => (this.loading = false));
    },
    autoSelectTrustee() {
      if (!this.isTrusteeOwnerType) {
        this.selectedTrustee = null;
        this.trusteeData = [];
        return;
      }
      const trustees = this.individualTrustees || [];
      if (!trustees.length) {
        this.selectedTrustee = null;
        this.trusteeData = [];
        return;
      }

      if (trustees.length === 1) {
        this.applyTrusteeSelection(trustees[0]);
        return;
      }

      const identityNumber = this.formModel?.requestApplicant?.trustee?.identityNumber;

      const matched =
        trustees.find(sec => identityNumber && sec.identityNumber == identityNumber) || trustees[0];

      if (matched) {
        this.applyTrusteeSelection(matched);
      }
    },
    trusteeChanged(value) {
      if (!value) {
        this.clearTrusteeSelection();
        return;
      }
      this.loading = true;
      const sec =
        (this.formModel.trustees || []).find(x => x.objectKey == value) ||
        (this.formModel.trustees || []).find(x => x.identityNumber == value);
      if (sec) {
        this.applyTrusteeSelection(sec);
      }
      this.loading = false;
    },
    clearTrusteeSelection() {
      this.selectedTrustee = null;
      this.trusteeData = [];
    },
    applyTrusteeSelection(sec) {
      if (!sec) return;
      this.selectedTrustee = sec;
      this.$set(this.formModel.requestApplicant, 'trusteeIdentityNumber', sec.identityNumber || '');
      this.$set(this.formModel.requestApplicant, 'requestOwnerTypeId', this.requestOwnerTypeEnum.trustee);
      this.trusteeData = [
        {
          fullName: sec.name,
          identityNumber: sec.identityNumber,
          identityTypeName: socialIdTypeEnum[sec.identityTypeId],
        },
      ];
      this.$set(this.formModel.requestApplicant, 'trustee', sec);
      this.$set(this.formModel.requestApplicant.trustee, 'isIdentityVerified', true);
    },
    stakeholderTypeChanged() {
      this.$set(this.formModel.requestApplicant, 'partner',  this.isPartner ? {} : null);
      this.$set(this.formModel.requestApplicant, 'debtorEmployee', this.isDebtorEmployee ? {} : null);
      this.$set(this.formModel.requestApplicant, 'creditor', this.isCreditor ? {} : null);
      this.$set(this.formModel.requestApplicant, 'interestedParty', this.isInterestParty ? {} : null);
    },
    requestOwnerTypeChanged() {
      if (this.isDebtor) {
        this.$set(this.formModel.requestApplicant, 'debtorEmployee', null);
        this.$set(this.formModel.requestApplicant, 'partner', null);
        this.$set(this.formModel.requestApplicant, 'creditor', null);
        this.$set(this.formModel.requestApplicant, 'interestedParty', null);
        this.$set(this.formModel.requestApplicant, 'bankruptId', this.formModel.requestApplicant.debtor.bankruptId);
      }
      else {
        this.$set(this.formModel.requestApplicant, 'bankruptId', null);
      }
      this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', null);
      if (this.isTrusteeOwnerType) {
        this.selectedTrustee = null;
        this.trusteeData = [];
        this.$set(this.formModel.requestApplicant, 'trusteeIdentityNumber', null);
        this.autoSelectTrustee();
      }
    },
    setCurrenUserInfo() {
      const names = [
        this.userInfo.firstName,
        this.userInfo.fatherName,
        this.userInfo.grandFatherName,
        this.userInfo.familyName,
      ];
      const fullName = names.filter(n => n).join(' ');
      const socialTypeId = this.userInfo.socialIdType;
      this.$set(this.formModel.requestApplicant, 'creditor', null);
      this.$set(this.formModel.requestApplicant, 'debtorEmployee', null);
      this.$set(this.formModel.requestApplicant, 'partner', null);
      this.$set(this.formModel.requestApplicant, 'interestedParty', null);
      this.$set(this.formModel.requestApplicant, 'applicantName', fullName);
      this.$set(this.formModel.requestApplicant, 'fullName', this.userInfo.fullName);
      this.$set(this.formModel.requestApplicant, 'identityTypeId', socialTypeId);
      this.$set(this.formModel.requestApplicant, 'identityNumber', this.userInfo.socialId);
      this.$set(this.formModel.requestApplicant, 'firstName', this.userInfo.firstName);
      this.$set(this.formModel.requestApplicant, 'fatherName', this.userInfo.fatherName);
      this.$set(this.formModel.requestApplicant, 'thirdName', this.userInfo.grandFatherName);
      this.$set(this.formModel.requestApplicant, 'familyName', this.userInfo.familyName);
      this.$set(this.formModel.requestApplicant, 'dateOfBirth', this.userInfo.dateOfBirth);
      this.$set(this.formModel.requestApplicant, 'nationality', socialTypeId === socialIdentityTypeEnum.nationalId ? countriesEnum.saudiArabia : this.userInfo.nationalityCountryId);
      this.$set(this.formModel.requestApplicant, 'gender', this.userInfo.gender);
    },

    interestPartyIdentityVerified(value){
      if (value) {
        this.loading = true;
        const payload = this.getInquiryDetails(this.formModel.requestApplicant.interestedParty);
        this.inquiryMainRequestInterestParty(payload)
          .then((res) => {
            if (res.data) {
              res.data.isRetrieved = true;
              this.$set(this.formModel.requestApplicant, 'interestedParty', res.data);
            }
          })
          .finally(() => (this.loading = false));
      }
    },
    partnerIdentityVerified(value) {
      if (value) {
        this.loading = true;
        const payload = this.getInquiryDetails(this.formModel.requestApplicant.partner);
        this.inquiryMainRequestPartner(payload)
          .then((res) => {
            if (res.data) {
              res.data.isRetrieved = true;
              this.$set(this.formModel.requestApplicant, 'partner', res.data);
            }
          })
          .finally(() => (this.loading = false));
      }
    },
    debtorEmployeeIdentityVerified(value) {
      if (value) {
        this.loading = true;
        const payload = this.getInquiryDetails(this.formModel.requestApplicant.debtorEmployee);
        this.inquiryMainRequestDebtorEmployees(payload)
          .then((res) => {
            if (res.data) {
              res.data.isRetrieved = true;
              this.$set(this.formModel.requestApplicant, 'debtorEmployee', res.data);
            }
          })
          .finally(() => (this.loading = false));
      }
    },
    creditorIdentityVerified(value) {
      if (value) {
        this.loading = true;
        const payload = this.getInquiryDetails(this.formModel.requestApplicant.creditor);
        this.inquiryMainRequestCreditor(payload)
          .then((res) => {
            if (res.data) {
              res.data.isRetrieved = true;
              this.$set(this.formModel.requestApplicant, 'creditor', res.data);
            }
          })
          .finally(() => (this.loading = false));
      }
    },
    getInquiryDetails(formModel) {
      const inquiryDetails = { objectKey: this.formModel.mainRequest.objectKey };
      this.inquiryFields.forEach((prop) => { this.$set(inquiryDetails, prop, formModel[prop]); });
      return inquiryDetails;
    },
  },

};
</script>

<style lang="scss" scoped>
</style>
